import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import ThreadStatusBadges from '../../components/ThreadStatusBadges/ThreadStatusBadges';
import SeoHead from '../../components/Seo/SeoHead';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import messagingService from '../../services/messagingService';
import { getCharacterProfilePath } from '../../utils/characterPaths';
import {
  getStaffAvatarClass,
  getStaffFrameClass,
  getStaffRoleBadgeClass,
  getStaffRoleBadgeLabel,
  staffStyles,
} from '../../utils/userStaffRole';
import styles from './UserProfile.module.css';

const PAGE_SIZE = 15;

const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMonthYear = (year, month) => {
  if (!year || !month) return null;
  return new Date(year, month - 1, 1).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });
};

const threadTypeLabel = (type) => {
  if (type === 'roleplay') return 'RP';
  if (type === 'character_sheet') return 'Fiche';
  return 'HRP';
};

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('characters');

  const [participatedThreads, setParticipatedThreads] = useState([]);
  const [participatedPagination, setParticipatedPagination] = useState(null);
  const [participatedLoading, setParticipatedLoading] = useState(false);
  const [participatedPage, setParticipatedPage] = useState(1);

  const [recentPosts, setRecentPosts] = useState([]);
  const [postsPagination, setPostsPagination] = useState(null);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsPage, setPostsPage] = useState(1);

  const [messagingLoading, setMessagingLoading] = useState(false);
  const [messagingError, setMessagingError] = useState('');

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getUserProfile(id);
      setProfile(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Profil introuvable');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const loadParticipatedThreads = useCallback(async (page, append = false) => {
    setParticipatedLoading(true);
    try {
      const data = await userService.getUserParticipatedThreads(id, page, PAGE_SIZE);
      setParticipatedThreads((prev) => (append ? [...prev, ...data.items] : data.items));
      setParticipatedPagination(data.pagination);
      setParticipatedPage(page);
    } finally {
      setParticipatedLoading(false);
    }
  }, [id]);

  const loadRecentPosts = useCallback(async (page, append = false) => {
    setPostsLoading(true);
    try {
      const data = await userService.getUserRecentPosts(id, page, PAGE_SIZE);
      setRecentPosts((prev) => (append ? [...prev, ...data.items] : data.items));
      setPostsPagination(data.pagination);
      setPostsPage(page);
    } finally {
      setPostsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setParticipatedThreads([]);
    setRecentPosts([]);
    setParticipatedPage(1);
    setPostsPage(1);
    void loadProfile();
  }, [id]);

  useEffect(() => {
    if (activeSection === 'threads' && participatedThreads.length === 0 && !participatedLoading) {
      void loadParticipatedThreads(1);
    }
  }, [activeSection, participatedThreads.length, participatedLoading, loadParticipatedThreads]);

  useEffect(() => {
    if (activeSection === 'posts' && recentPosts.length === 0 && !postsLoading) {
      void loadRecentPosts(1);
    }
  }, [activeSection, recentPosts.length, postsLoading, loadRecentPosts]);

  const handleSendMessage = async () => {
    if (!profile?.user?.id) return;
    setMessagingLoading(true);
    setMessagingError('');
    try {
      const data = await messagingService.createConversation({ targetUserId: profile.user.id });
      const conversationId = data?.conversation?.id;
      if (conversationId) {
        navigate(`/messagerie/${conversationId}`);
      } else {
        setMessagingError('Impossible d’ouvrir la conversation.');
      }
    } catch (err) {
      setMessagingError(err.response?.data?.error || 'Impossible d’ouvrir la conversation.');
    } finally {
      setMessagingLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout wide>
        <Loading message="Chargement du profil..." />
      </Layout>
    );
  }

  if (error || !profile?.user) {
    return (
      <Layout wide>
        <ErrorMessage message={error || 'Profil introuvable'} onRetry={loadProfile} />
      </Layout>
    );
  }

  const { user, stats, activity, trophies, characters, isSelf, canMessage } = profile;

  const breadcrumbItems = [
    { name: 'Accueil', url: '/', icon: 'home' },
    { name: user.pseudo, url: null, icon: 'character' },
  ];

  const seo = {
    metaTitle: `${user.pseudo} — Earth Forum`,
    metaDescription: `Profil de ${user.pseudo} sur Earth Forum`,
    robotsIndex: true,
  };

  const staffRoleBadgeLabel = getStaffRoleBadgeLabel(user.staffRole);

  return (
    <Layout wide>
      <SeoHead seo={seo} />
      <div className={styles.content}>
        <Breadcrumb items={breadcrumbItems} />

        <header className={`${styles.profileHeader} ${getStaffFrameClass(user.staffRole)}`}>
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.pseudo}
              className={`${styles.profileAvatar} ${getStaffAvatarClass(user.staffRole)}`}
            />
          ) : (
            <div
              className={`${styles.profileAvatarPlaceholder} ${getStaffAvatarClass(user.staffRole)}`}
              aria-hidden="true"
            >
              {(user.pseudo || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className={styles.profileHeaderBody}>
            <div className={styles.profileTitleRow}>
              <h1 className={styles.profileTitle}>{user.pseudo}</h1>
            </div>
            {staffRoleBadgeLabel && (
              <p className={staffStyles.staffRoleLine}>
                <span className={`${staffStyles.staffRoleBadge} ${getStaffRoleBadgeClass(user.staffRole)}`}>
                  {staffRoleBadgeLabel}
                </span>
              </p>
            )}
            {user.createdAt && (
              <p className={styles.profileMeta}>Membre depuis {formatDate(user.createdAt)}</p>
            )}
            <div className={styles.activityBlock}>
              <p className={styles.activityLine}>
                Dernier message posté le{' '}
                {activity?.lastPostAt ? (
                  <time dateTime={activity.lastPostAt}>{formatDate(activity.lastPostAt)}</time>
                ) : (
                  <span className={styles.activityEmpty}>jamais</span>
                )}
              </p>
              <p className={styles.activityLine}>
                Dernier message RP posté le{' '}
                {activity?.lastRoleplayPostAt ? (
                  <time dateTime={activity.lastRoleplayPostAt}>{formatDate(activity.lastRoleplayPostAt)}</time>
                ) : (
                  <span className={styles.activityEmpty}>jamais</span>
                )}
              </p>
              {activity?.lastRoleplayPostByUniverse?.length > 0 && (
                <div className={styles.activityUniverseList}>
                  <span className={styles.activityUniverseTitle}>RP par univers</span>
                  <ul className={styles.activityUniverseItems}>
                    {activity.lastRoleplayPostByUniverse.map((entry) => (
                      <li key={entry.universe?.id ?? entry.universe?.slug} className={styles.activityUniverseItem}>
                        <span className={styles.activityUniverseName}>{entry.universe?.name}</span>
                        <time dateTime={entry.lastPostAt}>{formatDate(entry.lastPostAt)}</time>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className={styles.profileActions}>
              {isSelf && (
                <Link to="/profil" className={styles.profileActionLink}>
                  Modifier mon profil
                </Link>
              )}
              {!isSelf && canMessage && (
                <button
                  type="button"
                  className={styles.messageButton}
                  onClick={handleSendMessage}
                  disabled={messagingLoading}
                >
                  {messagingLoading ? 'Ouverture…' : 'Envoyer un message'}
                </button>
              )}
              {!isSelf && !currentUser && (
                <Link to="/login" className={styles.profileActionLink}>
                  Connectez-vous pour envoyer un message
                </Link>
              )}
            </div>
            {messagingError && <p className={styles.messagingError}>{messagingError}</p>}
          </div>
          <aside className={styles.trophyPanel} aria-label="Trophées">
            <h2 className={styles.trophyPanelTitle}>Trophées</h2>
            {trophies?.length > 0 ? (
              <ul className={styles.trophyList}>
                {trophies.map((trophy) => {
                  const periodLabel = formatMonthYear(trophy.year, trophy.month);
                  const key = `${trophy.type}-${trophy.universe?.slug}-${trophy.year}-${trophy.month}`;
                  return (
                    <li key={key} className={styles.trophyItem}>
                      {trophy.universe?.slug ? (
                        <Link
                          to={`/member-of-month/${trophy.universe.slug}`}
                          className={styles.trophyLink}
                          title={`${trophy.label} — ${trophy.universe.name}`}
                        >
                          <span className={styles.trophyIcon} aria-hidden="true">🏆</span>
                          <span className={styles.trophyContent}>
                            <span className={styles.trophyLabel}>{trophy.label}</span>
                            <span className={styles.trophyMeta}>
                              {trophy.universe.name}
                              {periodLabel ? ` · ${periodLabel}` : ''}
                            </span>
                          </span>
                        </Link>
                      ) : (
                        <div className={styles.trophyLink}>
                          <span className={styles.trophyIcon} aria-hidden="true">🏆</span>
                          <span className={styles.trophyContent}>
                            <span className={styles.trophyLabel}>{trophy.label}</span>
                            {periodLabel && <span className={styles.trophyMeta}>{periodLabel}</span>}
                          </span>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className={styles.trophyEmpty}>Aucun trophée pour le moment.</p>
            )}
          </aside>
        </header>

        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{stats?.postCount ?? 0}</span>
            <span className={styles.statLabel}>Messages</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{stats?.characterCount ?? 0}</span>
            <span className={styles.statLabel}>Personnages</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{stats?.threadsParticipatedCount ?? 0}</span>
            <span className={styles.statLabel}>Sujets participés</span>
          </div>
        </div>

        <div className={styles.sectionNav} role="tablist">
          <button
            type="button"
            role="tab"
            className={`${styles.sectionTab} ${activeSection === 'characters' ? styles.sectionTabActive : ''}`}
            onClick={() => setActiveSection('characters')}
          >
            Personnages ({characters?.length ?? 0})
          </button>
          <button
            type="button"
            role="tab"
            className={`${styles.sectionTab} ${activeSection === 'threads' ? styles.sectionTabActive : ''}`}
            onClick={() => setActiveSection('threads')}
          >
            Sujets participés ({stats?.threadsParticipatedCount ?? 0})
          </button>
          <button
            type="button"
            role="tab"
            className={`${styles.sectionTab} ${activeSection === 'posts' ? styles.sectionTabActive : ''}`}
            onClick={() => setActiveSection('posts')}
          >
            Messages ({stats?.postCount ?? 0})
          </button>
        </div>

        {activeSection === 'characters' && (
          characters?.length > 0 ? (
            <div className={styles.characterGrid}>
              {characters.map((character) => (
                <Link
                  key={character.id}
                  to={getCharacterProfilePath(character.id)}
                  className={styles.characterCard}
                >
                  <div className={styles.characterCardTop}>
                    {character.avatar ? (
                      <img src={character.avatar} alt={character.name} className={styles.characterCardAvatar} />
                    ) : (
                      <span className={styles.characterCardAvatarFallback}>
                        {(character.name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <div>
                      <h2 className={styles.characterCardName}>{character.name}</h2>
                      <div className={styles.characterCardMeta}>
                        {[
                          character.universe?.name,
                          character.elseworld?.name,
                          character.occupation,
                          character.moralAffiliation,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className={styles.emptyState}>Aucun personnage validé à afficher.</p>
          )
        )}

        {activeSection === 'threads' && (
          participatedThreads.length > 0 ? (
            <>
              <div className={styles.threadList}>
                {participatedThreads.map((thread) => (
                  <Link key={thread.threadId} to={`/threads/${thread.threadSlug}`} className={styles.threadItem}>
                    <div className={styles.threadItemHeader}>
                      <h3 className={styles.threadItemTitle}>{thread.title}</h3>
                      <ThreadStatusBadges thread={thread} />
                    </div>
                    <div className={styles.threadItemMeta}>
                      {thread.character && <span>{thread.character}</span>}
                      {thread.forum?.name && <span>{thread.forum.name}</span>}
                      {thread.forum?.universe?.name && <span>{thread.forum.universe.name}</span>}
                      <span>{thread.replyCount} réponse{thread.replyCount !== 1 ? 's' : ''}</span>
                      {(thread.lastPostAt || thread.updatedAt) && (
                        <span>Dernière activité : {formatDate(thread.lastPostAt || thread.updatedAt)}</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
              {participatedPagination?.hasNext && (
                <button
                  type="button"
                  className={styles.loadMoreButton}
                  disabled={participatedLoading}
                  onClick={() => loadParticipatedThreads(participatedPage + 1, true)}
                >
                  {participatedLoading ? 'Chargement…' : 'Charger plus'}
                </button>
              )}
            </>
          ) : participatedLoading ? (
            <Loading message="Chargement des sujets…" />
          ) : (
            <p className={styles.emptyState}>Aucun sujet participé.</p>
          )
        )}

        {activeSection === 'posts' && (
          recentPosts.length > 0 ? (
            <>
              <div className={styles.postList}>
                {recentPosts.map((post) => (
                  <Link
                    key={post.postId}
                    to={`/threads/${post.threadSlug}#post-${post.postId}`}
                    className={styles.postItem}
                  >
                    <div className={styles.postItemHeader}>
                      <span className={styles.postItemDate}>{formatDate(post.createdAt)}</span>
                      <span className={styles.postItemType}>{threadTypeLabel(post.threadType)}</span>
                    </div>
                    <h3 className={styles.postItemThread}>{post.threadTitle}</h3>
                    {post.excerpt && <p className={styles.postItemExcerpt}>{post.excerpt}</p>}
                    <div className={styles.postItemMeta}>
                      {post.character ? (
                        <span>En tant que {post.character}</span>
                      ) : (
                        <span>En tant que {post.author}</span>
                      )}
                      {post.forum?.name && <span>{post.forum.name}</span>}
                      {post.forum?.universe && <span>{post.forum.universe}</span>}
                    </div>
                  </Link>
                ))}
              </div>
              {postsPagination?.hasNext && (
                <button
                  type="button"
                  className={styles.loadMoreButton}
                  disabled={postsLoading}
                  onClick={() => loadRecentPosts(postsPage + 1, true)}
                >
                  {postsLoading ? 'Chargement…' : 'Charger plus'}
                </button>
              )}
            </>
          ) : postsLoading ? (
            <Loading message="Chargement des messages…" />
          ) : (
            <p className={styles.emptyState}>Aucun message publié.</p>
          )
        )}
      </div>
    </Layout>
  );
};

export default UserProfile;
